import styled, { css } from 'styled-components';
import {
  TextInput,
  TextInputPlaceholder,
  TextLabel,
  FontSizes,
} from '../typography/typography.styles';
import errorIcon from '../../assets/error.svg';
import { BaseMarginComponent } from '../base-margin/base-margin.component';
import { BREAKPOINTS } from '../../constants/breakpoints';

interface FieldProps {
  $error?: boolean;
  $maxInputHeight?: string;
  $borderNone?: boolean;
  $isDisabled?: boolean;
  $readOnly?: boolean;
}

export const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
`;

interface HidableContainerProps {
  $isHidden?: boolean;
  $isIncrementControls?: boolean;
}

export const HidableContainer = styled(
  BaseMarginComponent('div')
)<HidableContainerProps>`
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'block')};
`;

export const FieldContainer = styled(HidableContainer)`
  width: 100%;
  input {
    text-align: ${({ $isIncrementControls }) =>
      $isIncrementControls ? 'center' : 'left'};
  }
`;

export const InputWrapper = styled.div`
  position: relative;
`;

interface ISuffixPrefixProps {
  $isClickable?: boolean;
}

const suffixPrefixStyles = css`
  display: flex;
  flex-shrink: 0;
`;

export const InputPrefix = styled.span<ISuffixPrefixProps>`
  ${suffixPrefixStyles};
  pointer-events: ${({ $isClickable = false }) =>
    $isClickable ? 'auto' : 'none'};
`;

export const InputSuffix = styled.span<ISuffixPrefixProps>`
  ${suffixPrefixStyles};
  pointer-events: ${({ $isClickable = false }) =>
    $isClickable ? 'auto' : 'none'};
`;

export const inputFieldStyles = css<FieldProps>`
  --input-background-color: #fff;

  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  border: ${({ $borderNone, theme }) =>
    $borderNone ? 'none' : `1px solid ${theme.colors.baseColors.grey50}`};
  border-radius: 10px;
  padding: 1rem 1.6rem;
  outline: none;
  transition: border-color 0.2s;
  max-height: ${({ $maxInputHeight = '4rem' }) => $maxInputHeight};
  ${TextInput}
  font-size: ${FontSizes.medium};
  font-family: ${({ theme }) => theme.fonts.body.fontFamily} !important;
  cursor: text;
  background-color: var(--input-background-color);

  @media screen and (min-width: 768px) {
    font-size: ${FontSizes.normal};
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      --input-background-color: #f5f6f7;
      cursor: default !important;

      ${InputSuffix},
      ${InputPrefix} {
        pointer-events: none;
      }
    `}

  ${({ $readOnly }) =>
    $readOnly &&
    css`
      --input-background-color: #f5f6f7;
    `}
`;

export const InputField = styled.label<FieldProps>`
  background-color: #fff;
  ${inputFieldStyles};

  ${({ $error }) =>
    $error &&
    css`
      border-color: ${({ theme }) => theme.colors.input.errorBorderColor};
    `}

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.input.borderColorFocus};
  }

  ${({ $readOnly }) =>
    $readOnly &&
    css`
      &:focus-within {
        border-color: ${({ theme }) => theme.colors.baseColors.grey50};
      }
    `}

  input {
    color: ${({ theme }) => theme.colors.baseColors.grey150};
    display: block;
    width: 100%;
    border: none;
    font: inherit;
    padding: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    background-color: var(--input-background-color);

    &::-ms-reveal,
    &::-ms-clear {
      display: none;
    }

    &:focus {
      outline: none;
    }

    &:disabled {
      background: none;
      color: ${({ theme }) => theme.colors.baseColors.grey65};
      cursor: default !important;
      background-color: inherit;
      opacity: 1;
    }

    &::placeholder {
      ${TextInputPlaceholder}
      font-size: ${FontSizes.medium};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      @media screen and (min-width: 768px) {
        font-size: ${FontSizes.normal};
      }
    }
  }
`;

interface DeltaComponentProps {
  $isDisabled?: boolean;
}

export const DeltaInputContainer = styled.div<DeltaComponentProps>`
  display: flex;
  width: 100%;
  height: 40px;
  padding: 10px;
  position: absolute;
  bottom: 0;
  left: 0;
  cursor: pointer;
  justify-content: space-between;
  align-items: center;
  z-index: 99;

  button {
    display: block;
    width: 20px;
    height: 20px;
    cursor: pointer;
    border: 0;
    background-color: transparent;
    color: ${({ theme }) => theme.colors.baseColors.grey100};
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:focus {
      outline: none;
    }
  }
`;

interface Props {
  $isDisabled?: boolean;
}

export const ArrowIconContainer = styled.div<Props>`
  display: flex;
  position: absolute;
  bottom: 50%;
  transform: translateY(50%);
  cursor: pointer;
  justify-content: center;
  align-items: center;
  right: 1.6rem;

  svg path {
    transition: fill 0.2s;
  }
`;

export const InputSelect = styled.div<Props>`
  ${inputFieldStyles};
  cursor: pointer;
  padding: 1rem 5.2rem 1rem 1.6rem !important;

  &:focus {
    border-color: ${({ theme }) => theme.colors.baseColors.blue80};
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
    `}
  &:hover ${ArrowIconContainer} {
    svg path {
      fill: ${({ $isDisabled, theme }) =>
        $isDisabled ? theme.colors.baseColors.grey80 : theme.colors.primary};
    }
  }

  @media (max-width: 1100px) {
    padding: 1rem 4.2rem 1rem 1.6rem !important;
  }
`;

export const InputContainer = styled.div<FieldProps>`
  position: relative;

  ${InputField},
  ${InputSelect} {
    max-height: ${({ $maxInputHeight = '4rem' }) => $maxInputHeight};
  }

  ${TextLabel} {
    position: relative;
    display: inline-block;
  }
`;

export const IconsContainer = styled.div`
  display: flex;
`;

interface Props {
  $mt?: string;
}

export const InfoTextContainer = styled.div<Props>`
  pointer-events: none;
  margin-top: ${({ $mt = 0 }) => $mt};
  text-align: right;
  font-size: ${FontSizes.small};
  color: ${({ theme }) => theme.colors.baseColors.grey65};

  ${TextInputPlaceholder}
  span {
    ${TextInputPlaceholder}
  }
`;

export const InfoMessageText = styled.span`
  min-width: 0;

  @media (max-width: ${BREAKPOINTS.mobile.css}) {
    word-break: break-all;
  }
`;

export const ErrorIcon = styled.span`
  display: inline-block;
  width: 1.6rem;
  height: 1.6rem;
  background: url(${errorIcon}) no-repeat content-box;
`;

export const Hint = styled.div`
  display: flex;

  svg {
    width: 1.6rem;
    height: 1.6rem;
  }
`;

export const Note = styled(TextLabel).attrs({ as: 'span' })`
  color: #96999e;
  font-weight: 400;
`;
